import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {Panel, PanelHeader} from '@Components/panel';
import PosterEditorColorPanel from '@Panels/poster-editor-color-panel';
import type {RGBA} from '@Utils/color.util';
import {BackgroundColorOpacityOption} from '@Panels/poster-editor-item-background-panel/components/background-color-opacity-option';
import {BackgroundColorOption} from '@Panels/poster-editor-item-background-panel/components/background-color-option';
import {BackgroundTypeOption} from '@Panels/poster-editor-item-background-panel/components/background-type-option';
import type {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {ContextualMenu} from '@Components/contextual-menu';
import {BaseVectorRoundnessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-vector-roundness-option/base-vector-roundness-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromBackgroundOfTextSlideItem} from '@Hooks/poster-editor/useGetKeyFromBackgroundOfTextSlideItem';
import {usegetKeyOfFillOfBackgroundOfTextSlideItem} from '@Hooks/poster-editor/usegetKeyOfFillOfBackgroundOfTextSlideItem';

interface TextslideBackgroundPanelProps {
  panelId: string;
  onClose(): void;
}

enum BackgroundPanels {
  DEFAULT = 'DEFAULT',
  COLOR_PICKER = 'COLOR_PICKER',
}

export function TextslideBackgroundPanel({...props}: TextslideBackgroundPanelProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const slideItemRx = useGetKeyFromBackgroundOfTextSlideItem('rx');
  const slideItemFillType = usegetKeyOfFillOfBackgroundOfTextSlideItem('fillType');
  const slideItemfillColor = usegetKeyOfFillOfBackgroundOfTextSlideItem('fillColor');

  const [panelToShow, setPanelToShow] = useState(BackgroundPanels.DEFAULT);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);

  if (!activeItem?.isSlideshow()) {
    return null;
  }

  if (!activeSlide?.isTextSlide()) {
    return null;
  }

  if (slideItemRx === undefined) {
    return null;
  }

  if (slideItemFillType === undefined || !slideItemfillColor) {
    throw new Error('unable to get the fill properties of background of selected text slide');
  }

  const getHeader = (): ReactElement => {
    return (
      <PanelHeader
        onClose={(): void => {
          props.onClose();
        }}
        title={window.i18next.t('pmwjs_background')}
      />
    );
  };

  const onChangeColor = (fillColor: RGBA[], undoable?: boolean): void => {
    void activeItem.updateSlideFromObject(
      activeSlide.uid,
      {
        background: {
          fill: {
            fillColor,
          },
        },
      },
      {
        undoable,
      }
    );
  };

  const getColorPanel = (): ReactElement | null => {
    return (
      <PosterEditorColorPanel
        panelId={props.panelId}
        fillObject={{
          fillType: slideItemFillType,
          fillColor: slideItemfillColor,
        }}
        onChangeColor={onChangeColor}
        onBackButton={(): void => {
          setPanelToShow(BackgroundPanels.DEFAULT);
        }}
        initialSelectedColorIndex={selectedColorIndex}
        onClose={(): void => {
          props.onClose();
        }}
      />
    );
  };

  const updateOpacity = (fillAlpha: number, undoable = true): void => {
    const fillColor = activeSlide.background.fill.getFillColor().slice();
    for (let i = 0; i < fillColor.length; i++) {
      const color = fillColor[i].slice();
      color[3] = fillAlpha;
      fillColor[i] = color as RGBA;
    }
    void activeItem.updateSlideFromObject(
      activeSlide.uid,
      {
        background: {
          fill: {
            fillColor,
          },
        },
      },
      {
        undoable,
      }
    );
  };

  const updateRoundness = (val: number, undoable = true): void => {
    void activeItem.updateSlideFromObject(
      activeSlide.uid,
      {
        background: {
          rx: val,
          ry: val,
        },
      },
      {
        undoable,
      }
    );
  };

  const getBackgroundOptions = (): ReactElement => {
    return (
      <>
        <BackgroundColorOption
          panelId={props.panelId}
          fillObject={{fillType: slideItemFillType, fillColor: slideItemfillColor}}
          onClickColorFill={(index): void => {
            setSelectedColorIndex(index);
            setPanelToShow(BackgroundPanels.COLOR_PICKER);
          }}
        />
        <BackgroundColorOpacityOption
          fillObject={{
            fillType: slideItemFillType,
            fillColor: slideItemfillColor,
          }}
          updateOpacity={updateOpacity}
        />
        <BaseVectorRoundnessOption className="spacing-m-b-4" fillRoundness={slideItemRx} onChangeRoundness={updateRoundness} />
        <BackgroundTypeOption
          heightOffset={76}
          updateBackgroundFillType={updateFillType}
          fillObject={{
            fillType: slideItemFillType,
            fillColor: slideItemfillColor,
          }}
          panelId={props.panelId}
        />
      </>
    );
  };

  const updateFillType = (newType: FillTypes): void => {
    void activeItem.updateSlideFromObject(activeSlide.uid, {
      background: {
        fill: {
          fillType: newType,
          fillColor: activeSlide.background.fill.getColorForNewType(newType),
        },
      },
    });
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu hasMobileMorphingProperties hasMobileModalPadding contentClassName="spacing-m-0" id="text-slide-background-mobile-panel">
        {getBackgroundOptions()}
      </ContextualMenu>
    );
  };

  const getPanel = (): ReactElement | null => {
    switch (panelToShow) {
      case BackgroundPanels.COLOR_PICKER:
        return getColorPanel();
      case BackgroundPanels.DEFAULT:
      default:
        return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
    }
  };

  return getPanel();
}
